/**
 * ExigenciaEditor - Configuração do bloco de Exigência (pré-requisito)
 *
 * Enquanto o processo estiver parado neste bloco:
 *  - ele aguarda um ou mais acontecimentos para liberar (ex.: cobrança paga,
 *    contrato assinado, proposta aceita) — libera no PRIMEIRO que ocorrer;
 *  - as ações escolhidas ficam bloqueadas no sistema, com a mensagem e a
 *    política (bloquear / avisar / observar) configuradas aqui.
 *
 * A lista de ações bloqueáveis NÃO é fixa: vem da tabela
 * `workflow_guard_action_types` pela RPC `get_workflow_guard_action_catalog`,
 * podendo ser ampliada por módulo sem tocar no front.
 *
 * Contrato gravado em `workflow_nodes.config`:
 *   { node_kind: 'exigencia', wait_mode: 'evento' | 'evento_ou_prazo',
 *     event_codes: string[], guard_actions: string[],
 *     enforcement: 'ENFORCE' | 'WARN' | 'OBSERVE', guard_message: string,
 *     duration_minutes?: number,
 *     release_conditions?: { op, groups: [...] } }
 *
 * @version 1.2.2
 * RELEASE: 2026-09-04 v1.2.2 - Texto de apoio informando que a exigência também é
 *          atendida por fatos anteriores ao início do processo (proposta, contrato,
 *          cobrança ou cliente já existentes).
 * RELEASE: 2026-09-04 v1.2.1 - Seção "Liberar quando acontecer" oculta para

 *          Exigências: o mesmo campo (`event_codes`) já é editado na seção
 *          "Configuração da espera" do inspetor. Preferência do usuário.
 * RELEASE: 2026-09-04 v1.2.0 - Seção "Modelos prontos" removida: sobrescrevia
 *          vários campos de uma vez e gerava confusão. A configuração passa a
 *          ser feita diretamente nos campos (condição, ações...).
 * RELEASE: 2026-09-03 v1.1.0 - Condição de liberação (`release_conditions`):
 *          quando a condição já está satisfeita ao entrar no bloco, o processo
 *          não para. Novo modelo pronto "Exigir cliente cadastrado".
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial: múltiplos eventos de liberação,
 *          catálogo dinâmico de ações bloqueáveis e modelos prontos.
 */


import { useMemo, useState } from 'react';
import { Check, Search, ShieldAlert, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Ajuda } from './Ajuda';
import { WorkflowConditionField } from './WorkflowConditionField';
import { agruparEventosPorModulo } from '../workflowEventGroups';
import { ROTULO_MODULO, useWorkflowGuardActions } from '@/hooks/useWorkflowGuardActions';

/** Item do catálogo de eventos (mesmo formato usado pelo inspetor) */
interface ItemCatalogo {
  code: string;
  label: string;
  module?: string | null;
}

interface Props {
  config: Record<string, any>;
  setCfg: (chave: string, valor: unknown) => void;
  eventCatalog: ItemCatalogo[];
}

export function ExigenciaEditor({ config, setCfg, eventCatalog }: Props) {
  const { data: catalogoAcoes = [] } = useWorkflowGuardActions();

  // Compatibilidade: versões antigas gravavam um único `event_code`
  const eventos: string[] = useMemo(() => {
    if (Array.isArray(config.event_codes)) return config.event_codes as string[];
    return config.event_code ? [String(config.event_code)] : [];
  }, [config.event_codes, config.event_code]);

  const acoes: string[] = Array.isArray(config.guard_actions) ? config.guard_actions : [];

  const alternarEvento = (code: string) => {
    const atual = new Set(eventos);
    if (atual.has(code)) atual.delete(code);
    else atual.add(code);
    setCfg('event_codes', Array.from(atual));
    setCfg('event_code', null); // migra o formato antigo
  };

  const alternarAcao = (code: string) => {
    const atual = new Set(acoes);
    if (atual.has(code)) atual.delete(code);
    else atual.add(code);
    setCfg('guard_actions', Array.from(atual));
  };

  const rotuloEvento = (code: string) => eventCatalog.find((e) => e.code === code)?.label ?? code;
  const rotuloAcao = (code: string) => catalogoAcoes.find((a) => a.code === code)?.label ?? code;

  return (
    <div className="space-y-3 rounded-xl border bg-card p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-warning/15 text-warning">
          <ShieldAlert className="h-3.5 w-3.5" />
        </span>
        <Label className="flex items-center gap-1.5 text-xs font-medium">
          Exigência (pré-requisito)
          <Ajuda
            texto={
              'O processo fica parado aqui até um dos acontecimentos escolhidos ocorrer. Enquanto isso, as ações marcadas ficam bloqueadas. Ex.: aguardar "Cobrança paga" e bloquear "Gerar contrato". O requisito também é atendido por fatos anteriores ao processo: se a proposta, o contrato, a cobrança ou o cliente já existirem no negócio, a exigência libera sozinha. Para aguardar outro processo inteiro terminar, use o bloco de Subprocesso.'
            }

          />
        </Label>
      </div>

      {/* Condição que já libera a exigência --------------------------------- */}
      <WorkflowConditionField
        valor={config.release_conditions}
        onChange={(condicao) => setCfg('release_conditions', condicao)}
        titulo="Já liberar quando a situação for verdadeira"
        ajuda='Se a condição já estiver satisfeita quando o processo chegar aqui, a exigência não bloqueia nada. Ex.: "Possui cliente vinculado" — negócios que já têm cliente passam direto.'
        tituloModal="Condição de liberação da exigência"
        vazio="Sem condição: a exigência só libera pelo acontecimento escolhido."
      />


      {/* Eventos que liberam ----------------------------------------------
           v1.2.1: oculto para Exigências. O mesmo campo `event_codes` é
           editado na seção "Configuração da espera" do WorkflowInspector,
           mantendo a preferência do usuário e evitando duplicidade. */}
      {false && (
        <ListaBuscavel
          titulo="Liberar quando acontecer"
          ajuda="Escolha um ou mais acontecimentos. O processo segue no primeiro que ocorrer (ex.: pagamento confirmado OU contrato assinado)."
          placeholder="Buscar acontecimento"
          selecionados={eventos}
          rotuloDe={rotuloEvento}
          onAlternar={alternarEvento}
          grupos={useMemo(
            () =>
              agruparEventosPorModulo(eventCatalog as any).map((g) => ({
                titulo: g.modulo,
                itens: g.eventos.map((e) => ({ code: e.code, label: e.label })),
              })),
            [eventCatalog],
          )}
        />
      )}

      {/* Ações bloqueadas -------------------------------------------------- */}
      <ListaBuscavel
        titulo="Bloquear enquanto não for cumprido"
        ajuda="Lista vinda do catálogo do sistema (pode ser ampliada por módulo). Marque tudo o que não pode ser feito enquanto a exigência estiver aberta."
        placeholder="Buscar ação"
        selecionados={acoes}
        rotuloDe={rotuloAcao}
        onAlternar={alternarAcao}
        grupos={useMemo(() => {
          const mapa = new Map<string, Array<{ code: string; label: string }>>();
          catalogoAcoes.forEach((a) => {
            const chave = ROTULO_MODULO[a.module] ?? a.module;
            if (!mapa.has(chave)) mapa.set(chave, []);
            mapa.get(chave)!.push({ code: a.code, label: a.label });
          });
          return Array.from(mapa.entries()).map(([titulo, itens]) => ({ titulo, itens }));
        }, [catalogoAcoes])}
      />

      {/* Política ----------------------------------------------------------- */}
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5 text-xs">
          Política
          <Ajuda texto="Bloquear impede a ação; Avisar apenas alerta; Observar só registra na auditoria." />
        </Label>
        <Select value={String(config.enforcement || 'ENFORCE')} onValueChange={(v) => setCfg('enforcement', v)}>
          <SelectTrigger className="h-9 rounded-lg">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ENFORCE">Bloquear</SelectItem>
            <SelectItem value="WARN">Apenas avisar</SelectItem>
            <SelectItem value="OBSERVE">Somente observar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mensagem ----------------------------------------------------------- */}
      <div className="space-y-2">
        <Label className="text-xs">Mensagem de bloqueio</Label>
        <Input
          value={String(config.guard_message || '')}
          onChange={(e) => setCfg('guard_message', e.target.value.slice(0, 200))}
          placeholder="Ex.: A proposta precisa estar aceita para gerar o contrato."
          className="h-9 rounded-lg"
        />
      </div>
    </div>
  );
}

/**
 * Lista buscável com múltipla seleção e chips (mesmo padrão do gatilho).
 * Exportada para ser reaproveitada por outras seções (ex.: travas da tarefa
 * humana no inspetor), evitando duplicar o componente.
 */
export function ListaBuscavel({

  titulo,
  ajuda,
  placeholder,
  grupos,
  selecionados,
  rotuloDe,
  onAlternar,
}: {
  titulo: string;
  ajuda: string;
  placeholder: string;
  grupos: Array<{ titulo: string; itens: Array<{ code: string; label: string }> }>;
  selecionados: string[];
  rotuloDe: (code: string) => string;
  onAlternar: (code: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return grupos;
    return grupos
      .map((g) => ({
        ...g,
        itens: g.itens.filter(
          (i) => i.label.toLowerCase().includes(termo) || i.code.toLowerCase().includes(termo),
        ),
      }))
      .filter((g) => g.itens.length > 0);
  }, [grupos, busca]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs">
          {titulo}
          <Ajuda texto={ajuda} />
        </Label>
        <Badge variant="secondary" className="ml-auto">
          {selecionados.length}
        </Badge>
      </div>

      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-full justify-start rounded-lg font-normal">
            <Search className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
            {selecionados.length === 0 ? 'Escolher…' : 'Adicionar ou remover'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] rounded-xl p-0">
          <div className="border-b p-2">
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value.slice(0, 60))}
              placeholder={placeholder}
              className="h-8 rounded-lg"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {filtrados.length === 0 && (
              <p className="px-2 py-3 text-xs text-muted-foreground">Nada encontrado.</p>
            )}
            {filtrados.map((grupo) => (
              <div key={grupo.titulo} className="mb-1">
                <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {grupo.titulo}
                </p>
                {grupo.itens.map((item) => {
                  const marcado = selecionados.includes(item.code);
                  return (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => onAlternar(item.code)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60"
                    >
                      <Check className={`h-3.5 w-3.5 shrink-0 ${marcado ? 'text-primary' : 'opacity-0'}`} />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {selecionados.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selecionados.map((code) => (
            <Badge key={code} variant="secondary" className="gap-1 rounded-lg py-1 pl-2 pr-1 text-xs font-normal">
              <span className="max-w-[190px] truncate">{rotuloDe(code)}</span>
              <button
                type="button"
                aria-label={`Remover ${rotuloDe(code)}`}
                onClick={() => onAlternar(code)}
                className="rounded p-0.5 hover:bg-background/60"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
